import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Typography, TextField, MenuItem, Alert } from '@mui/material';
import sceneService from '../../utils/apiRequests/sceneService';
import ReusableModal from '../Modal/ReusableModal';
import useThemeStyles from '../../hooks/useThemeStyles';
import { getProjectTypeConfig } from '../../utils/projectTypeConfig';

const AddSceneModal = ({ open, onClose, onSceneCreated, chapterId, chapterName, projectType }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState('paragraph');
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { colors } = useThemeStyles();
  const typeConfig = getProjectTypeConfig(projectType);

  const sceneTypes = [
    { value: 'paragraph', label: 'Paragraph' },
    { value: 'dialogue', label: 'Dialogue' },
    { value: 'action', label: 'Action' },
    { value: 'description', label: 'Description' },
    { value: 'transition', label: 'Transition' },
    { value: 'heading', label: 'Heading' },
    { value: 'note', label: 'Note' },
  ];

  const outlineFieldSx = {
    '& .MuiInputLabel-root': { color: colors?.text?.secondary ?? 'text.secondary' },
    '& .MuiInputLabel-root.Mui-focused': { color: 'primary.main' },
    input: { color: colors?.text?.primary ?? 'text.primary' },
    '& .MuiOutlinedInput-root': {
      '& fieldset': { borderColor: colors?.border?.subtle ?? 'divider' },
      '&:hover fieldset': { borderColor: 'primary.main' },
      '&.Mui-focused fieldset': { borderColor: 'primary.main' },
    },
    '& input:-webkit-autofill': {
      WebkitBoxShadow: `0 0 0 100px ${colors?.paper?.background ?? '#fff'} inset`,
      WebkitTextFillColor: colors?.text?.primary ?? '#000',
      transition: 'background-color 5000s ease-in-out 0s',
    },
  };

  const outlineMultilineSx = {
    '& .MuiInputLabel-root': { color: colors?.text?.secondary ?? 'text.secondary' },
    '& .MuiInputLabel-root.Mui-focused': { color: 'primary.main' },
    '& .MuiInputBase-input': { color: colors?.text?.primary ?? 'text.primary' },
    '& .MuiOutlinedInput-root': {
      '& fieldset': { borderColor: colors?.border?.subtle ?? 'divider' },
      '&:hover fieldset': { borderColor: 'primary.main' },
      '&.Mui-focused fieldset': { borderColor: 'primary.main' },
    },
  };

  const selectSx = {
    '& .MuiInputLabel-root': { color: colors?.text?.secondary ?? 'text.secondary' },
    '& .MuiInputLabel-root.Mui-focused': { color: 'primary.main' },
    '& .MuiOutlinedInput-root': {
      '& fieldset': { borderColor: colors?.border?.subtle ?? 'divider' },
      '&:hover fieldset': { borderColor: 'primary.main' },
      '&.Mui-focused fieldset': { borderColor: 'primary.main' },
    },
    '& .MuiSelect-select': { color: colors?.text?.primary ?? 'text.primary' },
    '& .MuiSvgIcon-root': { color: colors?.text?.secondary ?? 'text.secondary' },
  };

  const handleCreate = async () => {
    if (!type.trim()) {
      setError('Scene type is required.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const sceneData = {
        chapterId,
        name: name.trim() || undefined,
        description: description.trim() || undefined,
        type: type.trim(),
        data: data || {},
      };

      const response = await sceneService.addScene(chapterId, sceneData);

      if (response?.data?.scene) {
        onSceneCreated(response.data.scene);
        setName('');
        setDescription('');
        setType('paragraph');
        setData({});
        setError('');
        setLoading(false);
        onClose();
      } else {
        setError(
          response?.error?.response?.data?.error || 'Failed to create scene. Please try again.'
        );
        setLoading(false);
      }
    } catch {
      setError('Failed to create scene. Please try again.');
      setLoading(false);
    }
  };

  const handleClose = () => {
    setName('');
    setDescription('');
    setType('paragraph');
    setData({});
    setError('');
    setLoading(false);
    onClose();
  };

  return (
    <ReusableModal
      open={open}
      onClose={handleClose}
      title={`Add New ${typeConfig.subUnitLabel}`}
      width={600}
      showCloseButton
      ariaLabelledby="add-scene-modal-title"
      ariaDescribedby="add-scene-modal-description"
      contentSx={{ gap: 0, display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}
    >
      <Box sx={{ flexShrink: 0 }}>
        <Typography
          id="add-scene-modal-description"
          variant="body2"
          sx={{
            color: colors?.text?.secondary ?? 'text.secondary',
            textAlign: 'center',
            mb: 2,
          }}
        >
          Adding to: {chapterName}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          minHeight: 160,
          pr: 1,
          '&::-webkit-scrollbar': { width: '8px' },
          '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: colors?.border?.subtle ?? 'rgba(0,0,0,0.2)',
            borderRadius: '4px',
            '&:hover': {
              backgroundColor: colors?.border?.primary ?? 'rgba(0,0,0,0.3)',
            },
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label={`${typeConfig.subUnitLabel} Name (Optional)`}
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            disabled={loading}
            placeholder={typeConfig.subUnitPlaceholder}
            sx={outlineFieldSx}
          />

          <TextField
            label="Description (Optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            rows={2}
            disabled={loading}
            placeholder="Brief description of what happens in this scene"
            sx={outlineMultilineSx}
          />

          <TextField
            select
            label="Scene Type"
            value={type}
            onChange={(e) => setType(e.target.value)}
            fullWidth
            required
            disabled={loading}
            sx={selectSx}
          >
            {sceneTypes.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          mt: 3,
          pt: 2,
          borderTop: `1px solid ${colors?.border?.subtle ?? 'divider'}`,
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            onClick={handleClose}
            variant="outlined"
            disabled={loading}
            sx={{
              color: colors?.text?.secondary ?? 'text.secondary',
              borderColor: colors?.border?.subtle ?? 'divider',
              '&:hover': {
                color: colors?.text?.primary ?? 'text.primary',
                borderColor: colors?.border?.primary ?? 'primary.main',
              },
            }}
          >
            Cancel
          </Button>

          <Button
            onClick={handleCreate}
            variant="contained"
            color="primary"
            disabled={!type.trim() || loading}
            sx={{ px: 2.5, py: 0.75, minWidth: 120 }}
          >
            {loading ? 'Creating...' : 'Create'}
          </Button>
        </Box>
      </Box>
    </ReusableModal>
  );
};

AddSceneModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSceneCreated: PropTypes.func.isRequired,
  chapterId: PropTypes.string.isRequired,
  chapterName: PropTypes.string.isRequired,
  // eslint-disable-next-line react/require-default-props
  projectType: PropTypes.string,
};

export default AddSceneModal;
